/**
 * レーンのカードの印の索引 — 共通部が決めた「変わったレーン 4 型」を、カードの id で引ける形にする。
 *
 * 4 型はステージ・業務フロー・業務・業務ルールである。索引は id で引くので、型が
 * 増えてもここの中身は変わらない。
 *
 * **ここは判定しない。** 直接の変更（追加 / 変更 / 削除）か配下に変更かは、共通部の
 * `propagatePjLaneChanges` が案件の変更の行と階層のエッジから決め、`getChangeHistory` の
 * `laneChanges` として返す。業務マップと案件の変更一覧が階層をそれぞれ辿ると、同じ案件の同じ
 * ステージが画面によって変わったり変わらなかったりする。ここは返った値を引き直すだけである。
 *
 * **カードに出す印は差分の 4 状態だけである。** 読取の `descendant`（配下に変更）は、ここで変更なしに
 * 畳む（`pjCardMarkOf`。Dhik 2026-09-29「PJモードの業務マップでも配下に変更を出さず、色もつけないでほしい」
 * の「でも」＝業務マップ以外も）。索引そのものは読取の変わり方のまま持つ — 画面一覧の「変更したものだけ見る」は
 * 部品だけが変わった画面も数えるので、索引から `descendant` を落とすと絞り込みまで変わる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件で変わったレーン 4 型の一覧を、カードの id から印（追加・変更・削除・変更なし。配下に変更は変更なしに畳む）を引く索引へ畳む。
 * @graph-connects SpecDiffState [references] レーンのカードの印の語彙
 */
import type { PjLaneChange } from '@cortex/domain-spec-console';

import type { SpecDiffState } from '../components/spec/SpecDiffState.js';

/**
 * 読取が返す変わり方。直接の追加・変更・削除と、配下に変更（`descendant`）の 4 つ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件で変わったノードの変わり方（読取の語彙）
 * @graph-connects none
 */
export type PjLaneChangeKind = PjLaneChange['changeKind'];

/**
 * カードの id から変わり方を引く索引。変わっていないノードはキーごと持たない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business レーンのカードの id から変わり方を引く索引
 * @graph-connects none
 */
export type PjLaneMarkIndex = ReadonlyMap<number, PjLaneChangeKind>;

/** 案件を選んでいないときの索引。 @graph-connects none */
export const EMPTY_PJ_LANE_MARK_INDEX: PjLaneMarkIndex = new Map();

/**
 * 変わったレーン 4 型の一覧を索引へ畳む。
 *
 * @param laneChanges 共通部が決めた変わったノード
 * @returns カードの id から変わり方を引く索引
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変わったレーン 4 型の一覧を、カードの id から変わり方を引く索引へ畳む。
 * @graph-connects none
 */
export function buildPjLaneMarkIndex(laneChanges: readonly PjLaneChange[]): PjLaneMarkIndex {
  return new Map(laneChanges.map((change) => [change.nodeId, change.changeKind]));
}

/**
 * 「直接の変更」と数える印。案件が自身の変更の行を持つ追加・変更・削除だけで、配下に変更と
 * 変更なしは入れない。
 * @graph-connects none
 */
const DIRECT_PJ_LANE_MARKS: ReadonlySet<PjLaneChangeKind | SpecDiffState> = new Set([
  'created',
  'updated',
  'deleted',
]);

/**
 * 変わり方（または印）が直接の変更（案件の変更の行を持つ追加・変更・削除）かどうか。
 *
 * **判定はここ 1 か所に置く。** 案件の変更一覧の 4 レーン（`withoutDescendantPjLaneChanges`）と
 * 業務マップの「変更したものだけ見る」（`keepDirectlyChangedLanes`）とカードの印（`pjCardMarkOf`）が
 * 同じ定義を使う（Dhik 2026-09-29「PJの変更一覧で出すものをchangeに紐付けられた変更のみとする」）。
 * 画面ごとに書くと、同じ案件の同じ業務が片方の画面にだけ出る。
 *
 * @param mark 読取の変わり方かカードの印。変わっていなければ undefined
 * @returns 追加・変更・削除なら true（配下に変更・変更なし・undefined は false）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変わり方が、案件の変更の行を持つ直接の変更（追加・変更・削除）かを判定する。
 * @graph-connects none
 */
export function isDirectPjLaneMark(
  mark: PjLaneChangeKind | SpecDiffState | undefined,
): mark is Exclude<SpecDiffState, 'unchanged'> {
  return mark !== undefined && DIRECT_PJ_LANE_MARKS.has(mark);
}

/**
 * 読取の変わり方 1 つを、カードに出す印にする。直接の変更はそのまま、配下に変更と変わっていない
 * ものは変更なし。
 *
 * @param kind 読取の変わり方。変わっていなければ undefined
 * @returns カードの印（追加・変更・削除・変更なし）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 読取の変わり方をカードの印にする（配下に変更は出さず変更なしに畳む）。
 * @graph-connects isDirectPjLaneMark [calls] 直接の変更かの判定
 */
export function pjCardMarkOf(kind: PjLaneChangeKind | undefined): SpecDiffState {
  return isDirectPjLaneMark(kind) ? kind : 'unchanged';
}

/**
 * 索引からカード 1 枚の印を引く。変わっていなければ、または配下に変更なら `unchanged`。
 *
 * @param index 索引
 * @param nodeId カードの id
 * @returns カードの印
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 索引からカード 1 枚の印を引く（変わっていない・配下に変更なら変更なし）。
 * @graph-connects pjCardMarkOf [calls] 変わり方をカードの印にする
 */
export function pjLaneMarkOf(index: PjLaneMarkIndex, nodeId: number): SpecDiffState {
  return pjCardMarkOf(index.get(nodeId));
}
